// Phase 15 M1 — Web Axios client with Bearer interceptor + refresh dedup.
//
// Parity with mobile/src/api/client.ts (PRD §M1 — "Refresh dedup: igual que
// mobile"). Differences:
//   • token store is localStorage-backed (authStore), not SecureStore;
//   • on hard logout we don't navigate from here (no router) — components
//     read the auth snapshot and bounce to /login via ProtectedRoute.
//
// Single refresh in flight: concurrent 401s share the same `refreshInFlight`
// Promise so we never lose the race condition where two parallel requests
// both blow a stale token and end up rotating refresh tokens twice.

import { API_VERSION_PREFIX, type TokenPair } from '@leyrochain/shared';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { authStore } from '../auth/store';

const REFRESH_PATH = `${API_VERSION_PREFIX}/auth/refresh`;

function resolveBaseUrl(): string {
  const fromEnv = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return '';
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshInFlight: Promise<TokenPair | null> | null = null;

async function performRefresh(client: AxiosInstance): Promise<TokenPair | null> {
  const refreshToken = authStore.getSnapshot().refreshToken;
  if (!refreshToken) {
    authStore.logout();
    return null;
  }
  try {
    const { data } = await client.post<TokenPair>(
      REFRESH_PATH,
      { refresh_token: refreshToken },
      { headers: { 'X-Refresh-Request': '1' } },
    );
    authStore.setSession(data);
    return data;
  } catch {
    authStore.logout();
    return null;
  }
}

export function createApiClient(baseURL: string = resolveBaseUrl()): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const access = authStore.getSnapshot().accessToken;
    if (access && !config.headers['X-Refresh-Request']) {
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const original = error.config as RetryConfig | undefined;
      if (status !== 401 || !original || original._retry || original.url?.endsWith(REFRESH_PATH)) {
        return Promise.reject(error);
      }
      original._retry = true;
      if (!refreshInFlight) {
        refreshInFlight = performRefresh(client).finally(() => {
          refreshInFlight = null;
        });
      }
      const tokens = await refreshInFlight;
      if (!tokens) return Promise.reject(error);
      original.headers.Authorization = `Bearer ${tokens.access_token}`;
      return client.request(original as AxiosRequestConfig);
    },
  );

  return client;
}

export const apiClient = createApiClient();

/**
 * Reset module-level refresh state. EXPORTED FOR TESTS ONLY — the singleton
 * `refreshInFlight` is reused across tests, so jsdom test suites that simulate
 * a 401 → refresh path must clear it between cases to avoid leaking the
 * previous Promise.
 */
export function __resetRefreshState(): void {
  refreshInFlight = null;
}
